import React, { useEffect, useState } from "react";
import { api } from "@/lib/auth";
import { Star, RefreshCw, ExternalLink, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_PLACE_ID = "ChIJO85YyS-D1moRf_V5m8-w6gQ";
const inp = "w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none font-mono";

function KeyForm({ settings, onSaved }) {
  const [key, setKey] = useState("");
  const [placeId, setPlaceId] = useState(settings?.google_place_id || DEFAULT_PLACE_ID);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!key.trim()) return toast.error("Paste your API key first");
    setSaving(true);
    try {
      await api.put("/admin/settings", { google_places_api_key: key.trim(), google_place_id: placeId.trim() || DEFAULT_PLACE_ID, google_reviews_enabled: true });
      await api.post("/admin/google-reviews/refresh");
      toast.success("Connected to Google"); setKey(""); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="mt-5 grid md:grid-cols-2 gap-4" data-testid="google-key-form">
      <div>
        <label className="eyebrow block mb-1">Google Maps API key</label>
        <input type="password" value={key} onChange={e => setKey(e.target.value)} placeholder="AIza…" className={inp} data-testid="google-api-key-input" autoComplete="off" />
      </div>
      <div>
        <label className="eyebrow block mb-1">Place ID</label>
        <input value={placeId} onChange={e => setPlaceId(e.target.value)} className={inp} data-testid="google-place-id-input" />
      </div>
      <div className="md:col-span-2 flex items-center gap-4">
        <button onClick={save} disabled={saving} className="btn-red !py-2 !px-4 !text-xs disabled:opacity-50" data-testid="google-key-save">{saving ? "Connecting…" : "Save & connect"}</button>
        <a href="https://console.cloud.google.com/apis/library/places-backend.googleapis.com" target="_blank" rel="noreferrer" className="text-zinc-500 text-xs underline">Get a key: Google Cloud Console → Enable “Places API (New)” → Credentials</a>
      </div>
    </div>
  );
}

export default function GoogleReviews() {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [g, s] = await Promise.all([
        api.get("/google-reviews"),
        api.get("/admin/settings"),
      ]);
      setData(g.data);
      setSettings(s.data);
    } catch (e) {
      toast.error("Failed to load Google reviews");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/admin/google-reviews/refresh");
      setData(r.data);
      toast.success("Refreshed from Google");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const configured = data?.key_configured || !!settings?.google_places_api_key;

  return (
    <div className="max-w-6xl">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="eyebrow mb-2 flex items-center gap-2">
            <img
              src="https://www.gstatic.com/marketing-cms/assets/images/d5/dc/cfe9ce8b4425b410b49b7f2dd3f3/g.webp=s96-fcrop64=1,00000000ffffffff-rw"
              alt="Google"
              className="h-4 w-4 opacity-80"
            />
            Google Reviews
          </div>
          <h1 className="font-display text-4xl uppercase leading-none">Live from Google</h1>
          <p className="text-zinc-500 text-sm mt-2 max-w-xl">
            Real ratings and comments pulled straight from your Google Business profile. Cached for 1 hour to stay under Google's quota.
          </p>
        </div>
        <div className="flex gap-2">
          {configured && (
            <button
              onClick={refresh}
              disabled={refreshing}
              className="btn-ghost !py-2 !px-3 !text-xs inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="google-reviews-refresh-btn"
            >
              <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
              {refreshing ? "Refreshing…" : "Refresh from Google"}
            </button>
          )}
          {data?.google_maps_uri && (
            <a
              href={data.google_maps_uri}
              target="_blank"
              rel="noreferrer"
              className="btn-ghost !py-2 !px-3 !text-xs inline-flex items-center gap-2"
              data-testid="google-reviews-maps-link"
            >
              Open on Google Maps <ExternalLink size={12} />
            </a>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm">Loading…</div>
      ) : !configured ? (
        <div className="card-surface p-8 border border-amber-500/30" data-testid="google-reviews-not-configured">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-amber-400 shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <div className="font-display text-xl uppercase mb-1">Add your Google Maps API key</div>
              <p className="text-zinc-400 text-sm">
                Your Place ID is already set. Paste a Google Cloud API key with <span className="text-white">Places API (New)</span> enabled and reviews will start flowing to the homepage and Reviews page automatically (refreshed hourly). The key is stored server-side and never sent to visitors.
              </p>
              <KeyForm settings={settings} onSaved={load} />
            </div>
          </div>
        </div>
      ) : data?.error ? (
        <div className="card-surface p-6 border border-red-500/30 text-red-400" data-testid="google-reviews-error">
          Google returned an error: <span className="font-mono">{data.error}</span>. Double-check the Place ID and API key in Settings.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="card-surface p-6" data-testid="stat-rating">
              <div className="eyebrow mb-2">Average rating</div>
              <div className="flex items-baseline gap-2">
                <div className="font-display text-5xl text-white">{data?.rating?.toFixed(1) ?? "—"}</div>
                <div className="text-red-500 flex">
                  {[...Array(Math.round(data?.rating || 0))].map((_,i) => <Star key={`star-${i}`} size={14} fill="currentColor" />)}
                </div>
              </div>
            </div>
            <div className="card-surface p-6" data-testid="stat-total">
              <div className="eyebrow mb-2">Total ratings</div>
              <div className="font-display text-5xl text-white">{data?.user_ratings_total ?? "—"}</div>
            </div>
            <div className="card-surface p-6 md:col-span-2" data-testid="stat-fetched">
              <div className="eyebrow mb-2">Last fetched</div>
              <div className="text-white text-sm">{data?.fetched_at ? new Date(data.fetched_at).toLocaleString() : "—"}</div>
              <div className="text-zinc-500 text-xs mt-1">Auto-refreshes every hour. Click Refresh to force an update.</div>
            </div>
          </div>

          <div className="eyebrow mb-4">Recent Google comments ({(data?.reviews || []).length})</div>
          {(!data?.reviews || data.reviews.length === 0) ? (
            <div className="text-zinc-500 text-sm">No individual reviews returned by Google yet.</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {data.reviews.map((r, i) => (
                <div key={r.time || `${r.author_name}-${i}`} className="card-surface p-6" data-testid={`google-review-row-${i}`}>
                  <div className="flex items-center gap-3 mb-3">
                    {r.author_photo && <img src={r.author_photo} alt="" className="w-9 h-9 rounded-full" referrerPolicy="no-referrer" />}
                    <div className="min-w-0">
                      <div className="text-white text-sm truncate">{r.author_name}</div>
                      <div className="text-zinc-500 text-[10px]">{r.relative_time_description}</div>
                    </div>
                    <div className="ml-auto flex gap-1 text-red-500">
                      {[...Array(r.rating || 5)].map((_,i) => <Star key={`star-${i}`} size={12} fill="currentColor" />)}
                    </div>
                  </div>
                  <p className="text-zinc-300 text-sm whitespace-pre-line">"{r.text}"</p>
                  {r.google_maps_uri && (
                    <a
                      href={r.google_maps_uri}
                      target="_blank"
                      rel="noreferrer"
                      className="text-red-500 text-[10px] uppercase tracking-widest mt-3 inline-flex items-center gap-1"
                      data-testid={`google-review-reply-${i}`}
                    >
                      Reply on Google <ExternalLink size={10} />
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
